import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MousePointerClick } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { fetchAllRows } from '@/lib/supabaseHelpers';

interface LogoClick {
  current_step: number;
  device_type: string | null;
  created_at: string;
}

const LiveLogoClicks = () => {
  const [clicks, setClicks] = useState<LogoClick[]>([]);
  const [totalPageviews, setTotalPageviews] = useState(0);

  const fetchData = async () => {
    const [clicksData, pvData] = await Promise.all([
      fetchAllRows<LogoClick>('quiz_logo_clicks', 'current_step, device_type, created_at'),
      fetchAllRows<{ id: string }>('quiz_pageviews', 'id', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
    ]);
    setClicks(clicksData);
    setTotalPageviews(pvData.length);
  };

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('logo-clicks-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'quiz_logo_clicks' }, () => {
        fetchData();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const total = clicks.length;
  const pct = totalPageviews > 0 ? ((total / totalPageviews) * 100).toFixed(1) : '0';

  // Group by screen
  const screenCounts: Record<number, number> = {};
  clicks.forEach(c => {
    screenCounts[c.current_step] = (screenCounts[c.current_step] || 0) + 1;
  });
  const screenData = Object.entries(screenCounts)
    .map(([step, count]) => ({ name: `Scherm ${step}`, kliks: count }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
        <MousePointerClick className="w-4 h-4" />
        Logo-kliks → Productpagina
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        {total} kliks ({pct}% van {totalPageviews} pageviews)
      </p>

      {screenData.length > 0 ? (
        <>
          <p className="text-xs text-gray-400 mb-2">Op welk scherm klikken ze?</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={screenData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="kliks" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </>
      ) : (
        <p className="text-sm text-gray-400 italic">Nog geen logo-kliks geregistreerd</p>
      )}
    </div>
  );
};

export default LiveLogoClicks;
